import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import PropertyCard from "@/components/PropertyCard";
import HeroSearchBar from "@/components/HeroSearchBar";
import { CategoryChips } from "@/components/PageBlocks";
import { api } from "@/lib/api";
import { CITIES_ALL, PROPERTY_TYPES } from "@/lib/brand";
import {
    Search, SlidersHorizontal, X, ChevronLeft, ChevronRight,
    LayoutGrid, HardHat, Home as HomeIcon, Sparkles, Crown, Building2,
    ShieldCheck, BadgeCheck, ScrollText, Landmark, IndianRupee,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2400&q=80";

const HERO_STATS = [
    { v: "250+",  l: "Verified Projects" },
    { v: "10K+",  l: "Happy Customers" },
    { v: "15+",   l: "Years of Trust" },
];

const CATS = [
    { key: "all",      label: "All Properties",  sub: "12,560+",  icon: LayoutGrid },
    { key: "under",    label: "Under Construction", sub: "5,230+", icon: HardHat },
    { key: "ready",    label: "Ready to Move",   sub: "4,120+",   icon: HomeIcon },
    { key: "launch",   label: "New Launch",      sub: "1,890+",   icon: Sparkles },
    { key: "luxury",   label: "Luxury Homes",    sub: "1,320+",   icon: Crown },
    { key: "com",      label: "Commercial",      sub: "980+",     icon: Building2 },
];

const VERIFIED_PILLARS = [
    { icon: ShieldCheck, l: "Legally Verified" },
    { icon: BadgeCheck,  l: "RERA Verified" },
    { icon: ScrollText,  l: "Title Verified" },
    { icon: Landmark,    l: "Builder Approved" },
    { icon: Landmark,    l: "Bank Approved" },
    { icon: IndianRupee, l: "Zero Hidden Cost" },
];

const PAGE_SIZE = 12;

export default function Properties() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState("relevance");

    const cat = params.get("cat") || "all";
    const currentFilters = useMemo(() => ({
        city: params.get("city") || "All",
        type: params.get("type") || "All",
        status: params.get("status") || "All",
        listing_type: params.get("listing_type") || "sale",
        bhk: params.get("bhk") || "",
        min_price: params.get("min_price") || "",
        max_price: params.get("max_price") || "",
        q: params.get("q") || "",
    }), [params]);

    useEffect(() => {
        setLoading(true);
        const query = {};
        Object.entries(currentFilters).forEach(([k, v]) => { if (v && v !== "All") query[k] = v; });
        if (cat === "under") query.status = "Under Construction";
        if (cat === "ready") query.status = "Ready to Move";
        if (cat === "launch") query.status = "New Launch";
        if (cat === "com") query.type = "Commercial";
        if (cat === "auction") query.listing_type = "auction";
        api.get("/properties", { params: query })
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
        setPage(1);
    }, [currentFilters, cat]);

    const sorted = useMemo(() => {
        const arr = [...items];
        if (cat === "luxury") return arr.filter((x) => (x.price || 0) >= 3).sort((a, b) => (b.price || 0) - (a.price || 0));
        if (sort === "price_low")  return arr.sort((a, b) => (a.price || 0) - (b.price || 0));
        if (sort === "price_high") return arr.sort((a, b) => (b.price || 0) - (a.price || 0));
        if (sort === "trust")      return arr.sort((a, b) => (b.trust_score?.total || 0) - (a.trust_score?.total || 0));
        if (sort === "newest")     return arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        return arr;
    }, [items, sort, cat]);

    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value || value === "All") next.delete(key);
        else next.set(key, value);
        setParams(next);
    };

    const clearAll = () => setParams(new URLSearchParams(cat !== "all" ? { cat } : {}));

    const FilterBlock = () => (
        <div className="space-y-8">
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-3">Intent</div>
                <div className="flex gap-2">
                    {[{ k: "sale", l: "Buy" }, { k: "rent", l: "Rent" }].map((o) => (
                        <button key={o.k} data-testid={`filter-intent-${o.k}`} onClick={() => updateParam("listing_type", o.k)} className={`px-4 py-2 text-[11px] uppercase tracking-[0.24em] font-bold border transition-colors ${currentFilters.listing_type === o.k ? "border-black bg-black text-white" : "border-black/20 text-black/70 hover:border-black"}`}>{o.l}</button>
                    ))}
                </div>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-3">Search</div>
                <div className="relative">
                    <input data-testid="filter-search" defaultValue={currentFilters.q} placeholder="Locality, developer, keyword" onKeyDown={(e) => e.key === "Enter" && updateParam("q", e.currentTarget.value)} className="w-full bg-white border border-black/15 px-3 py-3 pr-10 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                    <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40" />
                </div>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-3">City</div>
                <select data-testid="filter-city" value={currentFilters.city} onChange={(e) => updateParam("city", e.target.value)} className="w-full bg-white border border-black/15 px-3 py-3 text-sm outline-none focus:border-[var(--ebg-gold)]">
                    <option value="All">All Markets</option>
                    {CITIES_ALL.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-3">Property Type</div>
                <div className="grid grid-cols-2 gap-2">
                    {[["All", "All"], ...PROPERTY_TYPES.map((t) => [t, t])].map(([k, l]) => (
                        <button key={k} data-testid={`filter-type-${k.toLowerCase()}`} onClick={() => updateParam("type", k)} className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${currentFilters.type === k ? "border-black bg-black text-white" : "border-black/15 text-black/70 hover:border-black"}`}>{l}</button>
                    ))}
                </div>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-3">Bedrooms</div>
                <div className="flex flex-wrap gap-2">
                    {["", "1", "2", "3", "4", "5"].map((b) => (
                        <button key={b || "any"} data-testid={`filter-bhk-${b || "any"}`} onClick={() => updateParam("bhk", b)} className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${currentFilters.bhk === b ? "border-black bg-black text-white" : "border-black/15 text-black/70 hover:border-black"}`}>{b ? `${b}+ BHK` : "Any"}</button>
                    ))}
                </div>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-3">Budget</div>
                <div className="grid grid-cols-2 gap-2">
                    <input data-testid="filter-min-price" type="number" placeholder="Min ₹ Cr" defaultValue={currentFilters.min_price} onBlur={(e) => updateParam("min_price", e.target.value)} className="bg-white border border-black/15 px-3 py-3 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                    <input data-testid="filter-max-price" type="number" placeholder="Max ₹ Cr" defaultValue={currentFilters.max_price} onBlur={(e) => updateParam("max_price", e.target.value)} className="bg-white border border-black/15 px-3 py-3 text-sm outline-none focus:border-[var(--ebg-gold)]"/>
                </div>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/60 mb-3">Verified Status</div>
                <div className="space-y-2 text-[13px] text-black/70">
                    {["RERA Verified", "Legally Verified", "Bank Approved", "Ready Possession"].map((s) => (
                        <label key={s} className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" className="accent-[var(--ebg-gold)]"/> {s}
                        </label>
                    ))}
                </div>
            </div>
            <button data-testid="filter-clear-all" onClick={clearAll} className="text-[11px] uppercase tracking-[0.24em] font-bold text-black link-gold">Clear all filters</button>
        </div>
    );

    return (
        <PageShell navVariant="dark">
            {/* ============ HERO ============ */}
            <section data-testid="properties-hero" className="relative bg-[#0a0d18] text-white overflow-hidden">
                <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45"/>
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0d18]/70 via-[#0a0d18]/50 to-[#0a0d18]"/>
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-36 pb-16 md:pt-44 md:pb-20">
                    <div className="text-[10px] tracking-[0.28em] font-bold text-[var(--ebg-gold)] uppercase mb-5">Verified Properties</div>
                    <h1 className="font-serif text-4xl md:text-6xl lg:text-[72px] leading-[1.02] max-w-4xl">
                        Premium Properties.<br/>
                        <em className="not-italic text-[var(--ebg-gold)]">Verified for Your Peace of Mind.</em>
                    </h1>
                    <p className="text-white/70 max-w-2xl mt-6 leading-relaxed text-base md:text-lg">
                        Discover handpicked properties with 120+ point legal verification, RERA registration,
                        and complete transparency at every step.
                    </p>
                    <div className="mt-10">
                        <HeroSearchBar/>
                    </div>
                    <div className="mt-12 flex items-center gap-10 flex-wrap">
                        {HERO_STATS.map((s, i) => (
                            <div key={i} data-testid={`props-hero-stat-${i}`}>
                                <div className="display-num text-2xl md:text-3xl text-white tabular-nums leading-none">{s.v}</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60 mt-1.5">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ CATEGORY CHIPS ============ */}
            <section data-testid="prop-categories" className="bg-white border-b border-black/8 sticky top-20 z-30">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5">
                    <CategoryChips
                        items={CATS}
                        activeKey={cat}
                        onSelect={(k) => updateParam("cat", k === "all" ? "" : k)}
                        testidPrefix="prop-cat"
                    />
                </div>
            </section>

            {/* ============ SIDEBAR + GRID ============ */}
            <div className="bg-[var(--ebg-ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 pb-24 pt-10 grid lg:grid-cols-12 gap-10">
                    <aside data-testid="filters-sidebar" className="hidden lg:block lg:col-span-3 bg-white border border-black/8 p-6 sticky top-[164px] self-start">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-black/10">
                            <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-black">Filters</span>
                            <button onClick={clearAll} className="text-[11px] text-[var(--ebg-gold)] hover:text-black">Clear</button>
                        </div>
                        <FilterBlock />
                        <button data-testid="filter-apply" className="mt-6 w-full bg-[var(--ebg-gold)] text-black py-3 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-black hover:text-white transition-colors">Apply Filters</button>
                    </aside>

                    <button data-testid="filters-mobile-toggle" onClick={() => setMobileFiltersOpen(true)} className="lg:hidden flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-black border border-black/20 px-4 py-3 mb-6 w-fit">
                        <SlidersHorizontal size={16} /> Filters
                    </button>

                    {mobileFiltersOpen && (
                        <div data-testid="filters-mobile-panel" className="fixed inset-0 z-[60] bg-[var(--ebg-ivory)] overflow-y-auto lg:hidden">
                            <div className="p-6 flex items-center justify-between border-b border-black/10">
                                <span className="text-[11px] uppercase tracking-[0.28em] font-bold">Filters</span>
                                <button data-testid="filters-mobile-close" onClick={() => setMobileFiltersOpen(false)}><X /></button>
                            </div>
                            <div className="p-6">
                                <FilterBlock />
                                <button data-testid="filters-mobile-apply" onClick={() => setMobileFiltersOpen(false)} className="mt-8 w-full bg-black text-white py-4 text-[11px] uppercase tracking-[0.28em] font-bold">
                                    Show {total} residences
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="lg:col-span-9">
                        {/* Sort bar */}
                        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                            <div className="text-[13px] text-black/60" data-testid="results-count">
                                {loading ? "Loading…" : (
                                    <><span className="text-black font-bold">{total}</span> residences {cat !== "all" ? `in ${CATS.find(c => c.key === cat)?.label}` : "found"}</>
                                )}
                            </div>
                            <label className="flex items-center gap-2 text-[13px]">
                                <span className="text-black/50 text-[11px] uppercase tracking-[0.2em] font-bold">Sort by</span>
                                <select data-testid="props-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-black/15 px-3 py-2 text-[12px] outline-none focus:border-[var(--ebg-gold)]">
                                    <option value="relevance">Most Relevant</option>
                                    <option value="trust">Trust Score</option>
                                    <option value="price_low">Price: Low → High</option>
                                    <option value="price_high">Price: High → Low</option>
                                    <option value="newest">Newest First</option>
                                </select>
                            </label>
                        </div>

                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-12" data-testid="properties-grid">
                            {pageItems.map((p, i) => <PropertyCard key={p.id} item={p} index={i} />)}
                        </div>

                        {!loading && total === 0 && (
                            <div className="py-20 text-center border border-dashed border-black/15" data-testid="no-results">
                                <p className="font-serif text-2xl">No residences match these filters.</p>
                                <p className="text-black/50 mt-2 text-sm">Try clearing filters or expanding your criteria.</p>
                                <button onClick={clearAll} className="mt-6 text-[11px] uppercase tracking-[0.24em] font-bold text-black link-gold">Reset filters</button>
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-12 flex items-center justify-center gap-2" data-testid="pagination">
                                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="w-10 h-10 border border-black/15 flex items-center justify-center disabled:opacity-40 hover:border-black">
                                    <ChevronLeft size={16}/>
                                </button>
                                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                                    const p = i + 1;
                                    return (
                                        <button key={p} onClick={() => setPage(p)} data-testid={`page-${p}`} className={`w-10 h-10 text-[13px] font-bold border transition-colors ${page === p ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black"}`}>{p}</button>
                                    );
                                })}
                                {totalPages > 5 && <span className="px-2 text-black/40">…</span>}
                                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="w-10 h-10 border border-black/15 flex items-center justify-center disabled:opacity-40 hover:border-black">
                                    <ChevronRight size={16}/>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ============ VERIFIED PROMISE ============ */}
            <section data-testid="verified-strip" className="bg-white border-y border-black/8">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-16">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Every Property. Every Detail.</div>
                        <h2 className="font-serif text-3xl md:text-4xl mt-2 leading-tight">100% Verified.</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                        {VERIFIED_PILLARS.map((v, i) => (
                            <div key={i} data-testid={`verified-pillar-${i}`} className="text-center">
                                <span className="mx-auto w-14 h-14 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center">
                                    <v.icon size={20} strokeWidth={1.8}/>
                                </span>
                                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-black mt-4">{v.l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ NOT FINDING? CTA ============ */}
            <section className="relative bg-[#0a0d18] text-white overflow-hidden" data-testid="properties-advisor-cta">
                <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=2400&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30"/>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18] via-[#0a0d18]/80 to-[#0a0d18]/40"/>
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <h3 className="font-serif text-3xl md:text-4xl leading-[1.05]">Not finding what<br/>you're looking for?</h3>
                        <p className="text-white/65 mt-4 max-w-lg leading-relaxed">
                            Our advisory team can help you find off-market and exclusive properties tailored to your specific needs.
                        </p>
                    </div>
                    <div className="lg:col-span-5 lg:justify-self-end w-full lg:w-auto">
                        <Link to="/contact" data-testid="properties-advisor-btn" className="bg-[var(--ebg-gold)] text-black px-10 py-4 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-white transition-colors inline-flex items-center justify-center w-full lg:w-auto">
                            Talk to an Advisor
                        </Link>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
